/**
 * Whether the backend is still gathering a property's details (public
 * records, listing photos and price) after it was added.
 *
 * The backend starts enrichment right after the property is saved and marks
 * it `pending`; a run takes about 1–2 minutes and gives up after 20. A
 * brand-new property may not carry a status yet, so the first few minutes
 * after creation count as in progress too.
 */

import type { Product } from '@/types/ProductType';

/** How often to refetch while a property is being enriched. */
export const ENRICHMENT_POLL_MS = 5000;

/** Treat a just-created property as in progress until the backend marks it. */
const NEW_PROPERTY_GRACE_MS = 3 * 60 * 1000;
/** Matches the backend's run timeout, plus a little slack. */
const PENDING_TIMEOUT_MS = 22 * 60 * 1000;

const ageMs = (iso?: string, now = Date.now()) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(t) ? now - t : Infinity;
};

export function isEnrichmentInProgress(
  product: Pick<Product, 'enrichment' | 'createdAt'> | undefined,
  now = Date.now()
): boolean {
  if (!product) return false;
  const status = product.enrichment?.status;
  if (status === 'pending') {
    return (
      ageMs(product.enrichment?.requestedAt ?? product.createdAt, now) <
      PENDING_TIMEOUT_MS
    );
  }
  if (status) return false;
  return ageMs(product.createdAt, now) < NEW_PROPERTY_GRACE_MS;
}

/** Listing photos in display order, without blanks or repeats. */
export function productPhotos(
  product: Pick<Product, 'photo' | 'photos'> | undefined
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of [product?.photo, ...(product?.photos ?? [])]) {
    const u = typeof url === 'string' ? url.trim() : '';
    if (u && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}
